import React, { useState, useEffect } from 'react';
import { CheckCircleIcon } from '@heroicons/react/24/outline';
import { memberBookingAPI } from '../../services/api';
import { clinicTimeToUTC } from '../../utils/timezone';
import { formatBookingDateTime, formatEndTime } from '../../utils/timezone';
import { BookingListItem } from '../../types/booking';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { apiErrorMessage } from '../../utils/apiError';

const PodBooking: React.FC = () => {
  const today = new Date();
  const todayStr = format(today, 'yyyy-MM-dd');

  const [date, setDate] = useState(todayStr);
  const [startTime, setStartTime] = useState('09:00');
  const [durationMinutes, setDurationMinutes] = useState(30);
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [dayBookings, setDayBookings] = useState<BookingListItem[]>([]);
  const [loadingSchedule, setLoadingSchedule] = useState(false);

  const cost = (durationMinutes / 30) * 5;

  // Fetch clinician's bookings for the selected date
  useEffect(() => {
    const fetchDaySchedule = async () => {
      setLoadingSchedule(true);
      try {
        const bookings = await memberBookingAPI.getMyBookings();
        const selectedDate = date;
        const filtered = bookings.filter((b: BookingListItem) => {
          const bookingDate = b.start_time.split('T')[0];
          return bookingDate === selectedDate && b.status !== 'cancelled';
        });
        // Sort by start time
        filtered.sort((a: BookingListItem, b: BookingListItem) => a.start_time.localeCompare(b.start_time));
        setDayBookings(filtered);
      } catch (e) {
        console.warn('Could not load day schedule');
      } finally {
        setLoadingSchedule(false);
      }
    };
    fetchDaySchedule();
  }, [date]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const [hourStr, minStr] = startTime.split(':');
    const hour = parseInt(hourStr, 10);
    const minute = parseInt(minStr, 10);
    const startTimeISO = clinicTimeToUTC(date, hour, minute);

    setIsSubmitting(true);
    try {
      await memberBookingAPI.bookPod({
        start_time: startTimeISO,
        duration_minutes: durationMinutes,
        notes: notes.trim() || undefined,
      });
      setSuccess(true);
    } catch (error: any) {
      const detail = apiErrorMessage(error, 'Failed to book pod');
      toast.error(detail);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="max-w-md mx-auto bg-white rounded-lg shadow p-8 text-center">
        <CheckCircleIcon className="mx-auto h-16 w-16 text-clinic-primary" />
        <h2 className="mt-4 text-2xl font-bold text-clinic-dark">Pod Reserved</h2>
        <p className="mt-2 text-clinic-muted">Your pod has been booked. Cost: ${cost.toFixed(2)}</p>
        <div className="mt-6 flex justify-center gap-4">
          <button
            onClick={() => { setSuccess(false); setNotes(''); }}
            className="px-6 py-2 bg-clinic-primary text-white rounded-lg hover:bg-clinic-primary-dark transition-colors"
          >
            Book Another Pod
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto">
      <h2 className="text-2xl font-bold text-clinic-dark mb-2">Book a Pod</h2>
      <p className="text-clinic-muted mb-6">
        Reserve an individual workspace. Pods are billed at $5.00 per 30 minutes.
      </p>

      <div className="bg-white rounded-lg shadow p-6 space-y-4">
        {/* Date picker */}
        <div>
          <label className="block text-sm font-medium text-clinic-dark mb-1">Date</label>
          <input
            type="date"
            value={date}
            min={todayStr}
            onChange={(e) => setDate(e.target.value)}
            className="w-full px-3 py-2 border border-clinic-primary-light rounded-lg focus:outline-none focus:ring-2 focus:ring-clinic-primary/30 focus:border-clinic-primary"
            required
          />
        </div>

        {/* Mini day schedule */}
        <div>
          <label className="block text-sm font-medium text-clinic-dark mb-2">Your schedule for this day</label>
          <div className="border border-clinic-primary-lighter rounded-lg overflow-hidden">
            {loadingSchedule ? (
              <div className="p-3 text-center text-clinic-muted text-sm">Loading...</div>
            ) : dayBookings.length === 0 ? (
              <div className="p-3 text-center text-clinic-muted text-sm">No bookings this day - you're free!</div>
            ) : (
              <div className="divide-y divide-clinic-primary-lighter">
                {dayBookings.map((booking) => {
                  const { time } = formatBookingDateTime(booking.start_time);
                  const endTimeStr = formatEndTime(booking.start_time, booking.duration_minutes + (booking.buffer_minutes || 0));
                  return (
                    <div key={booking.id} className="px-3 py-2 flex items-center gap-3 bg-clinic-cream/30">
                      <div className="w-1 h-8 rounded-full bg-clinic-gold" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-clinic-dark">
                          {time} - {endTimeStr}
                        </p>
                        <p className="text-xs text-clinic-muted">
                          {booking.is_self_booked ? 'Self-Booked' : (booking.patient_name || 'Client')}
                          {booking.room_name ? ` · ${booking.room_name}` : ''}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Booking form */}
        <form onSubmit={handleSubmit} className="space-y-4" data-guide="pod-form">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-clinic-dark mb-1">Start Time</label>
              <input
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                className="w-full px-3 py-2 border border-clinic-primary-light rounded-lg focus:outline-none focus:ring-2 focus:ring-clinic-primary/30 focus:border-clinic-primary"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-clinic-dark mb-1">Duration</label>
              <select
                value={durationMinutes}
                onChange={(e) => setDurationMinutes(parseInt(e.target.value, 10))}
                className="w-full px-3 py-2 border border-clinic-primary-light rounded-lg focus:outline-none focus:ring-2 focus:ring-clinic-primary/30 focus:border-clinic-primary"
              >
                <option value={30}>30 min - $5</option>
                <option value={60}>1 hr - $10</option>
                <option value={90}>1.5 hr - $15</option>
                <option value={120}>2 hr - $20</option>
                <option value={180}>3 hr - $30</option>
                <option value={240}>4 hr - $40</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-clinic-dark mb-1">Notes (optional)</label>
            <input
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="What will you be working on?"
              className="w-full px-3 py-2 border border-clinic-primary-light rounded-lg focus:outline-none focus:ring-2 focus:ring-clinic-primary/30 focus:border-clinic-primary"
              maxLength={500}
            />
          </div>

          <div className="bg-clinic-cream/50 rounded-lg p-3 flex items-center justify-between">
            <span className="text-sm text-clinic-muted">Added to monthly invoice + GST</span>
            <span className="text-lg font-semibold text-clinic-dark">${cost.toFixed(2)}</span>
          </div>

          <button
            type="submit"
            data-guide="pod-submit"
            disabled={isSubmitting}
            className="w-full px-6 py-2.5 bg-clinic-primary text-white rounded-lg hover:bg-clinic-primary-dark disabled:opacity-50 transition-colors font-medium"
          >
            {isSubmitting ? 'Reserving...' : `Reserve Pod - $${cost.toFixed(2)}`}
          </button>
        </form>
      </div>
    </div>
  );
};

export default PodBooking;
